import React, { useState, useMemo } from "react";
import TableContainer from "@mui/material/TableContainer";
import TooltipComponent from "../TooltipComponent/TooltipComponent";
import { MaterialReactTable } from "material-react-table";
import CaptureButtons from "../CaptureButtons/CaptureButtons";

const PortfolioAnalyticsWithOptionsTable = ({
    optionsPortfolioTableData = [],
    equityPortfolioTableData= [],
    hasEquityData = false,
    hasOptionData = false
}) => {

const [showTable, setShowTable] = useState(true);

const optionAnalyticsTableData = useMemo(() => {
    if (optionsPortfolioTableData && Array.isArray(optionsPortfolioTableData) && optionsPortfolioTableData.length > 0) {
        if (optionsPortfolioTableData[0].hasOwnProperty('sr')) {
            return optionsPortfolioTableData;
        }
    }
    return [];
}, [optionsPortfolioTableData]);

const equityAnalyticsTableData = useMemo(() => {
    if (equityPortfolioTableData && Array.isArray(equityPortfolioTableData) && equityPortfolioTableData.length > 0) {
        if (equityPortfolioTableData[0].hasOwnProperty('sr')) {
            return equityPortfolioTableData;
        }
    }
    return [];
}, [equityPortfolioTableData]);

const combinedPortfolioAnalyticsData = useMemo(() => {
    const combined = [...optionAnalyticsTableData, ...equityAnalyticsTableData];
    return combined.map((item, index) => ({
        ...item,
        sr: index + 1
    }));
}, [optionAnalyticsTableData, equityAnalyticsTableData]);

const equityAnalyticsColumns= useMemo(() => [
    { accessorKey: "sr", header: "#", size: 70 },
    { accessorKey: "symbol", header: "Symbol", size: 120 },
    { accessorKey: "type", header: "Type", size: 80 },
    { accessorKey: "tradeDate", header: "Trade Date", size: 200 },
    { accessorKey: "latestClosePrice", header: "Close Price", size: 150, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "marketValue", header: "Market Value", size: 140, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "weight", header: "Weight", size: 120, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> }
], []);

const optionsAnalyticsColumns= useMemo(() => [
    { accessorKey: "sr", header: "#", size: 70 },
    { accessorKey: "symbol", header: "Symbol", size: 130 },
    { accessorKey: "optionType", header: "Option Type", size: 180 },
    { accessorKey: "strikePrice", header: "Strike Price (K)", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "stockBeta", header: "Beta (β)", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "stockPrice", header: "Stock Price", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "indexBarrier", header: "Index Barrier [Option of Hit the Strike]", size: 280, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "pitmUsingTheBetaEquation", header: "P(ITM) Using the Beta Equation", size: 280, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "expiryDate", header: "Expiry Date", size: 200 },
   
], []);

const portfolioAnalyticsColumns = useMemo(() => [
    { accessorKey: "sr", header: "#", size: 70 },
    { accessorKey: "symbol", header: "Symbol", size: 130 },
    { accessorKey: "type", header: "Type", size: 130 },
    { accessorKey: "quantity", header: "Quantity", size: 130, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? cell.row?.original?.quantity ?? ''}</div> },
    { accessorKey: "stockVolatility", header: "Stock Volatility", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "stockBeta", header: "Stock Beta", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "iv", header: "IV", size: 80, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    // { accessorKey: "mtm", header: "MTM", size: 120 },
    { accessorKey: "delta", header: "DELTA", size: 120, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "gamma", header: "GAMMA", size: 120, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "theta", header: "THETA", size: 120, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    // { accessorKey: "riskcontribution", header: "Risk Contribution", size: 120 },
    { accessorKey: "kHat", header: "K-hat", size: 120, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
    { accessorKey: "intrinsicValue", header: "Intrinsic Value", size: 130, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div>  },
    { accessorKey: "nominalWeight", header: "Nominal Weight (%)", size: 180, muiTableHeadCellProps: { sx: { textAlign: 'right' } }, muiTableBodyCellProps: { sx: { textAlign: 'right' } }, Cell: ({ cell }) => <div style={{ width: '100%', textAlign: 'right' }}>{cell.getValue?.() ?? ''}</div> },
], []);

const [tabletabvalue, setTableTabValue] = React.useState("Portfolio Analytics");

function handleTableTabChange(tabletabvalue) {
    setTableTabValue(tabletabvalue);
}

return (
    <div className="portfolio-table contributio-table nogap factortbl sum-container">
        <CaptureButtons 
            elementType='table' 
            chartName={
                tabletabvalue === "Portfolio Analytics" 
                    ? "Portfolio_Analytics_Table" 
                    : tabletabvalue === "Options Analytics" 
                    ? "Options_Analytics_Table" 
                    : "Equity_Analytics_Table"
            } 
            activeTab="Portfolio Analytics with Options" 
            tableHeaders={
                tabletabvalue === "Portfolio Analytics" 
                    ? portfolioAnalyticsColumns 
                    : tabletabvalue === "Options Analytics" 
                    ? optionsAnalyticsColumns 
                    : equityAnalyticsColumns
            } 
            tableData={
                tabletabvalue === "Portfolio Analytics" 
                    ? combinedPortfolioAnalyticsData 
                    : tabletabvalue === "Options Analytics" 
                    ? optionAnalyticsTableData 
                    : equityAnalyticsTableData
            }>
        </CaptureButtons>
        <div className="table_holder table_head ai-toggle-con m0i">
            <h3 onClick={() => setShowTable(!showTable)} className="ai-toggle-ttl">
                {showTable && <span className="minus">-</span>} {!showTable && <span>+</span>}
                <span className="tgltitle">Portfolio with Options Analytics Details</span>
            </h3>
            {showTable && (
                <>
                    <ul className="subnavbar-menu fixed">
                        <li>
                            <button
                                className={
                                    tabletabvalue === "Portfolio Analytics"
                                        ? "SUBITEM-selected "
                                        : "SUBITEM"
                                }
                                onClick={() =>
                                    handleTableTabChange("Portfolio Analytics")
                                }
                            >
                                Portfolio Analytics
                                <TooltipComponent id={"Portfolio Analytics"} />
                            </button>
                        </li>
                        <li>
                            <button
                                className={
                                    tabletabvalue === "Options Analytics"
                                        ? "SUBITEM-selected"
                                        : "SUBITEM"
                                }
                                onClick={() =>
                                    handleTableTabChange("Options Analytics")
                                }
                            >
                                Options Analytics
                                <TooltipComponent id={"Options Analytics"} />
                            </button>
                        </li>
                        {/* {hasEquityData && (
                            <li>
                                <button
                                    className={
                                        tabletabvalue === "Equity Analytics"
                                            ? "SUBITEM-selected"
                                            : "SUBITEM"
                                    }
                                    onClick={() =>
                                        handleTableTabChange("Equity Analytics")
                                    }
                                >
                                    Equity Analytics{" "}
                                    <TooltipComponent id={"Equity Analytics"} />
                                </button>
                            </li>
                        )} */}
                    </ul>
                    <TableContainer className="table_height tabbody">
                        {tabletabvalue === "Portfolio Analytics" && (
                            <MaterialReactTable
                                columns={portfolioAnalyticsColumns}
                                data={combinedPortfolioAnalyticsData}
                                enableStickyHeader
                                enableStickyFooter
                                enableBottomToolbar={false}
                                enableColumnActions={false}
                                enableDensityToggle={false}
                                enableColumnResizing
                                enableColumnOrdering
                                enableRowPinning
                                enablePagination={false}
                                enableFullScreenToggle={false}
                                muiTableContainerProps={{
                                sx: {
                                    maxHeight: "400px",
                                },
                                }}
                                initialState={{
                                    density: "compact",
                                }}
                                 muiTableHeadCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                    },
                                }}
                                muiTableFootCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 1,
                                    },
                                }}
                            />
                        )}
                        {tabletabvalue === "Options Analytics" && (
                            <MaterialReactTable
                                columns={optionsAnalyticsColumns}
                                data={optionAnalyticsTableData}
                                enableStickyHeader
                                enableStickyFooter
                                enableBottomToolbar={false}
                                enableColumnActions={false}
                                enableDensityToggle={false}
                                enableColumnResizing
                                enableColumnOrdering
                                enableRowPinning
                                enablePagination={false}
                                enableFullScreenToggle={false}
                                muiTableContainerProps={{
                                sx: {
                                    maxHeight: "400px",
                                },
                                }}
                                initialState={{
                                    density: "compact",
                                }}
                                 muiTableHeadCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                    },
                                }}
                                muiTableFootCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 1,
                                    },
                                }}
                                renderEmptyRowsFallback={() => (
                                    <div style={{ 
                                        padding: "40px 20px", 
                                        textAlign: "center" 
                                    }}>
                                        <p style={{ color: "#999", fontSize: "16px" }}>
                                        No options analytics data available. Please upload a portfolio with options data.
                                        </p>
                                    </div>
                                )}
                            />
                        )}
                        {/* {tabletabvalue === "Equity Analytics" && (
                            <MaterialReactTable
                                columns={equityAnalyticsColumns}
                                data={equityAnalyticsTableData}
                                enableStickyHeader
                                enableStickyFooter
                                enableBottomToolbar={false}
                                enableColumnActions={false}
                                enableDensityToggle={false}
                                enableColumnResizing
                                enableColumnOrdering
                                enableRowPinning
                                enablePagination={false}
                                enableFullScreenToggle={false}
                                muiTableContainerProps={{
                                sx: {
                                    maxHeight: "400px",
                                },
                                }}
                                initialState={{
                                    density: "compact",
                                }}
                                 muiTableHeadCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                    },
                                }}
                                muiTableFootCellProps={{
                                    sx: {
                                        backgroundColor: "white",
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 1,
                                    },
                                }}
                            />
                        )} */}
                    </TableContainer>
                </>
            )}
        </div>
    </div>
);
};

export default PortfolioAnalyticsWithOptionsTable;